"use client";

import React, { useState, useMemo } from "react";
import { Lightbulb } from "lucide-react";

const formatCurrency = (value: number) => {
  if (value >= 1e9) return `$${(value / 1e9).toFixed(1).replace(/\.0$/, "")}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(1).replace(/\.0$/, "")}K`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

export function EarnoutVisualizer() {
  const [baseValuation, setBaseValuation] = useState<number>(1000000000); // 1B Baseline
  const [founderEarnoutPct, setFounderEarnoutPct] = useState<number>(10); // 10% of base EV as earnout shares
  const [targetPrice, setTargetPrice] = useState<number>(18.0);

  // Meshflow standard Sponsor Promote (Assuming $345M Trust, standard 20% promote is ~8.625M shares)
  // Let's assume effectively 8.5M shares.
  const SPONSOR_SHARES = 8500000;

  // Pre-configured hurdles
  const HURDLE_1 = 12.5; // 50% of founder earnout unlocks
  const HURDLE_2 = 15.0; // Remaining 50% unlocks
  const SPONSOR_HURDLE = 18.0; // Sponsor promote becomes fully unrestricted / maximal value recognized here

  const currentPrice = 10.0; // Baseline De-SPAC price

  // Generate data for the AreaChart
  const chartData = useMemo(() => {
    const data = [];
    const baseShares = baseValuation / currentPrice; // 100M shares @ 10
    const earnoutShares =
      (baseValuation * (founderEarnoutPct / 100)) / currentPrice;

    for (let price = 10; price <= 30; price += 0.5) {
      // Calculate Base Value
      const matchedBaseValue = baseShares * price;

      // Calculate Founder Earnout unlocked value
      let unlockedEarnoutShares = 0;
      if (price >= HURDLE_1) unlockedEarnoutShares += earnoutShares * 0.5;
      if (price >= HURDLE_2) unlockedEarnoutShares += earnoutShares * 0.5;
      const founderEarnoutValue = unlockedEarnoutShares * price;

      // Calculate Sponsor Promote Value (Vested/Unlocked conceptually at $18, but holds value linearly,
      // though let's model "Fully Unlocked/Liquid Value" jumping at $18 for dramatic effect,
      // or just straight numerical value but highlighting alignment).
      // Standard promote is technically worthless in a liquidation, but here we just show its market value.
      let sponsorPromoteValue = SPONSOR_SHARES * price;

      data.push({
        price: price,
        "Target Equity Base Value": matchedBaseValue,
        "Unlocked Founder Earnout": founderEarnoutValue,
        "Sponsor Promote (At-Risk)":
          price < SPONSOR_HURDLE ? sponsorPromoteValue : 0,
        "Sponsor Promote (Liquid)":
          price >= SPONSOR_HURDLE ? sponsorPromoteValue : 0,
        totalFounderValue: matchedBaseValue + founderEarnoutValue,
      });
    }
    return data;
  }, [baseValuation, founderEarnoutPct]);

  const earnoutShares =
    (baseValuation * (founderEarnoutPct / 100)) / currentPrice;
  let unlockedEarnoutShares = 0;
  if (targetPrice >= HURDLE_1) unlockedEarnoutShares += earnoutShares * 0.5;
  if (targetPrice >= HURDLE_2) unlockedEarnoutShares += earnoutShares * 0.5;
  const founderEarnoutValue = unlockedEarnoutShares * targetPrice;
  const sponsorPromoteValue =
    targetPrice >= SPONSOR_HURDLE ? SPONSOR_SHARES * targetPrice : 0;

  let insightText = `At your target share price of $${targetPrice.toFixed(2)}, `;
  if (targetPrice >= HURDLE_2) {
    insightText += `founders unlock 100% of their earnout shares, yielding an additional ${formatCurrency(founderEarnoutValue)} in realized value. `;
  } else if (targetPrice >= HURDLE_1) {
    insightText += `founders unlock 50% of their earnout shares (Tranche 1), yielding an additional ${formatCurrency(founderEarnoutValue)}. Tranche 2 remains locked. `;
  } else {
    insightText += `founder earnout shares remain fully locked as the $${HURDLE_1.toFixed(2)} hurdle has not been met. `;
  }

  if (targetPrice >= SPONSOR_HURDLE) {
    insightText += `Furthermore, the sponsor promote is fully unrestricted, worth ${formatCurrency(sponsorPromoteValue)}.`;
  } else {
    insightText += `The sponsor promote remains at-risk until the stock sustains $${SPONSOR_HURDLE.toFixed(2)}.`;
  }

  return (
    <div className="w-full max-w-6xl mx-auto space-y-8">
      {/* Controls Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 p-6 bg-card rounded-2xl border border-border shadow-sm">
        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Target Company Base Valuation (EV)
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {formatCurrency(baseValuation)}
            </span>
          </label>
          <input
            type="range"
            min={500000000}
            max={5000000000}
            step={100000000}
            value={baseValuation}
            onChange={(e) => setBaseValuation(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Founder Earnout Pool (% of Base EV)
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              {founderEarnoutPct}%
            </span>
          </label>
          <input
            type="range"
            min={0}
            max={20}
            step={1}
            value={founderEarnoutPct}
            onChange={(e) => setFounderEarnoutPct(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>

        <div>
          <label className="flex items-center justify-between text-sm font-semibold mb-3">
            Target Share Price Simulation
            <span className="px-2 py-0.5 bg-background rounded-md border border-border font-mono text-primary">
              ${targetPrice.toFixed(2)}
            </span>
          </label>
          <input
            type="range"
            min={10}
            max={30}
            step={0.5}
            value={targetPrice}
            onChange={(e) => setTargetPrice(Number(e.target.value))}
            className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
          />
        </div>
      </div>

      {/* Main Chart */}
      <div className="p-6 bg-card rounded-2xl border border-border shadow-sm flex flex-col justify-center min-h-[500px] mt-8">
        <div className="mb-6">
          <h3 className="text-xl font-bold text-center">
            Absolute Alignment: Shared Upside Scenario
          </h3>
          <p className="text-sm text-center text-muted-foreground mt-2">
            Modeling equity value creation across VWAP price hurdles. Note how
            the Sponsor Promote remains "at-risk" until the $18.00 threshold is
            crossed.
          </p>
        </div>

        <div className="w-full h-[400px] relative font-sans">
          {/* Custom SVG Area Chart */}
          <svg
            viewBox="0 0 1000 400"
            className="w-full h-full overflow-visible"
            preserveAspectRatio="none"
          >
            {/* Grid & Axes */}
            <g
              className="text-muted-foreground/30"
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray="3 3"
            >
              {[0, 1, 2, 3, 4].map((i) => (
                <line
                  key={`h-${i}`}
                  x1="0"
                  y1={i * 100}
                  x2="1000"
                  y2={i * 100}
                />
              ))}
            </g>

            {/* Y Axis Labels (HTML overlay below) */}

            {/* X Axis Labels (HTML overlay below) */}

            {/* Threshold Lines */}
            <line
              x1={((HURDLE_1 - 10) / 20) * 1000}
              y1="0"
              x2={((HURDLE_1 - 10) / 20) * 1000}
              y2="400"
              stroke="#f59e0b"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <text
              x={((HURDLE_1 - 10) / 20) * 1000}
              y="-10"
              fill="#f59e0b"
              fontSize="12"
              textAnchor="middle"
              fontWeight="bold"
            >
              Earnout 1 (${HURDLE_1.toFixed(2)})
            </text>

            <line
              x1={((HURDLE_2 - 10) / 20) * 1000}
              y1="0"
              x2={((HURDLE_2 - 10) / 20) * 1000}
              y2="400"
              stroke="#f59e0b"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <text
              x={((HURDLE_2 - 10) / 20) * 1000}
              y="-10"
              fill="#f59e0b"
              fontSize="12"
              textAnchor="middle"
              fontWeight="bold"
            >
              Earnout 2 (${HURDLE_2.toFixed(2)})
            </text>

            <line
              x1={((SPONSOR_HURDLE - 10) / 20) * 1000}
              y1="0"
              x2={((SPONSOR_HURDLE - 10) / 20) * 1000}
              y2="400"
              stroke="#3b82f6"
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            <text
              x={((SPONSOR_HURDLE - 10) / 20) * 1000 + 5}
              y="15"
              fill="#3b82f6"
              fontSize="12"
              textAnchor="start"
              fontWeight="bold"
            >
              Sponsor Unlocked
            </text>

            {/* Areas */}
            {(() => {
              const maxVal =
                Math.max(
                  ...chartData.map(
                    (d) =>
                      d["Target Equity Base Value"] +
                      d["Unlocked Founder Earnout"] +
                      d["Sponsor Promote (At-Risk)"] +
                      d["Sponsor Promote (Liquid)"],
                  ),
                ) * 1.1 || 1;
              const getY = (val: number) => 400 - (val / maxVal) * 400;
              const getX = (price: number) => ((price - 10) / 20) * 1000;

              // Generate Paths
              let pathBase = "M 0 400 ";
              let pathEarnout = "M 0 400 ";
              let pathSponsorAtRisk = "M 0 400 ";
              let pathSponsorLiquid = "M 0 400 ";

              let prevPrice = 10;
              let prevY1 = 0,
                prevY2 = 0,
                prevY3 = 0,
                prevY4 = 0;

              chartData.forEach((d) => {
                const x = getX(d.price);
                const y1 = d["Target Equity Base Value"];
                const y2 = y1 + d["Unlocked Founder Earnout"];
                const y3 = y2 + d["Sponsor Promote (At-Risk)"];
                const y4 = y3 + d["Sponsor Promote (Liquid)"];

                // Draw Base
                pathBase += `L ${x} ${getY(y1)} `;

                // Draw Earnout (Step function effect manually)
                if (d.price === HURDLE_1 || d.price === HURDLE_2) {
                  pathEarnout += `L ${x - 0.1} ${getY(y1 + (prevY2 - prevY1))} `;
                }
                pathEarnout += `L ${x} ${getY(y2)} `;

                // Draw Sponsor At-Risk
                if (d.price === SPONSOR_HURDLE) {
                  pathSponsorAtRisk += `L ${x - 0.1} ${getY(y2 + (prevY3 - prevY2))} `;
                }
                pathSponsorAtRisk += `L ${x} ${getY(y3)} `;

                // Draw Sponsor Liquid
                if (d.price === SPONSOR_HURDLE) {
                  pathSponsorLiquid += `L ${x - 0.1} ${getY(y3 + (prevY4 - prevY3))} `;
                }
                pathSponsorLiquid += `L ${x} ${getY(y4)} `;

                prevPrice = d.price;
                prevY1 = y1;
                prevY2 = y2;
                prevY3 = y3;
                prevY4 = y4;
              });

              // Close paths to bottom right, then bottom left
              pathBase += `L 1000 400 Z`;
              pathEarnout += `L 1000 ${getY(prevY1)} L 1000 400 L 0 400 Z`; // This is tricky, layering areas makes it easier.

              // Simpler approach for layering: just draw full polygons down to Y=0 (which is visually layered back-to-front).
              // Layer 4 (Largest/Top): Liquid
              let l4 = "M 0 400 ";
              chartData.forEach(
                (d) =>
                  (l4 += `L ${getX(d.price)} ${getY(d["Target Equity Base Value"] + d["Unlocked Founder Earnout"] + d["Sponsor Promote (At-Risk)"] + d["Sponsor Promote (Liquid)"])} `),
              );
              l4 += "L 1000 400 Z";
              // Layer 3: At Risk
              let l3 = "M 0 400 ";
              chartData.forEach((d) => {
                if (d.price === SPONSOR_HURDLE)
                  l3 += `L ${getX(d.price) - 0.1} ${getY(d["Target Equity Base Value"] + d["Unlocked Founder Earnout"] + d["Sponsor Promote (At-Risk)"])} `;
                l3 += `L ${getX(d.price)} ${getY(d["Target Equity Base Value"] + d["Unlocked Founder Earnout"] + d["Sponsor Promote (At-Risk)"])} `;
              });
              l3 += "L 1000 400 Z";
              // Layer 2: Earnout
              let l2 = "M 0 400 ";
              chartData.forEach((d, i) => {
                if (i > 0 && (d.price === HURDLE_1 || d.price === HURDLE_2)) {
                  const prev = chartData[i - 1];
                  l2 += `L ${getX(d.price) - 0.1} ${getY(d["Target Equity Base Value"] + prev["Unlocked Founder Earnout"])} `;
                }
                l2 += `L ${getX(d.price)} ${getY(d["Target Equity Base Value"] + d["Unlocked Founder Earnout"])} `;
              });
              l2 += "L 1000 400 Z";
              // Layer 1 (Smallest): Base
              let l1 = "M 0 400 ";
              chartData.forEach(
                (d) =>
                  (l1 += `L ${getX(d.price)} ${getY(d["Target Equity Base Value"])} `),
              );
              l1 += "L 1000 400 Z";

              return (
                <>
                  <path d={l4} fill="#3b82f6" fillOpacity={0.6} />
                  <path
                    d={l3}
                    fill="#ef4444"
                    fillOpacity={0.2}
                    stroke="#ef4444"
                    strokeWidth="2"
                  />
                  <path
                    d={l2}
                    fill="#f59e0b"
                    fillOpacity={0.6}
                    stroke="#f59e0b"
                    strokeWidth="2"
                  />
                  <path
                    d={l1}
                    fill="#10b981"
                    fillOpacity={0.2}
                    stroke="#10b981"
                    strokeWidth="2"
                  />
                </>
              );
            })()}
          </svg>

          {/* Interactive Tooltip Overlay (Invisible grid) */}
          <div className="absolute inset-0 flex justify-between">
            {chartData.map((d, i) => (
              <div key={i} className="flex-1 h-full group/hover relative">
                <div className="absolute top-0 bottom-0 left-1/2 w-[1px] bg-foreground/20 opacity-0 group-hover/hover:opacity-100 transition-opacity"></div>

                {/* Tooltip */}
                <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 opacity-0 group-hover/hover:opacity-100 pointer-events-none transition-opacity bg-card border border-border shadow-2xl rounded-xl p-4 z-50 w-64">
                  <p className="text-sm border-b border-border pb-2 mb-2">
                    Stock Price:{" "}
                    <span className="font-bold text-foreground font-mono">
                      ${d.price.toFixed(2)}
                    </span>
                  </p>
                  <div className="space-y-1 text-xs font-mono">
                    {d["Sponsor Promote (Liquid)"] > 0 && (
                      <p className="flex justify-between text-[#3b82f6]">
                        <span className="font-sans">Sponsor (Liquid)</span>
                        {formatCurrency(d["Sponsor Promote (Liquid)"])}
                      </p>
                    )}
                    {d["Sponsor Promote (At-Risk)"] > 0 && (
                      <p className="flex justify-between text-[#ef4444]">
                        <span className="font-sans">Sponsor (At-Risk)</span>
                        {formatCurrency(d["Sponsor Promote (At-Risk)"])}
                      </p>
                    )}
                    {d["Unlocked Founder Earnout"] > 0 && (
                      <p className="flex justify-between text-[#f59e0b]">
                        <span className="font-sans">Unlocked Earnout</span>
                        {formatCurrency(d["Unlocked Founder Earnout"])}
                      </p>
                    )}
                    <p className="flex justify-between text-[#10b981]">
                      <span className="font-sans">Base Equity Value</span>
                      {formatCurrency(d["Target Equity Base Value"])}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Y-Axis HTML Overlays */}
          <div className="absolute top-0 bottom-0 -left-14 w-12 flex flex-col justify-between text-right text-xs text-muted-foreground font-mono pointer-events-none z-10">
            {(() => {
              const maxVal =
                Math.max(
                  ...chartData.map(
                    (d) =>
                      d["Target Equity Base Value"] +
                      d["Unlocked Founder Earnout"] +
                      d["Sponsor Promote (At-Risk)"] +
                      d["Sponsor Promote (Liquid)"],
                  ),
                ) * 1.1 || 1;
              return [
                maxVal,
                maxVal * 0.75,
                maxVal * 0.5,
                maxVal * 0.25,
                0,
              ].map((v, i) => (
                <span key={i} className="-mt-2">
                  {v >= 1e9
                    ? `$${(v / 1e9).toFixed(1)}B`
                    : `$${(v / 1e6).toFixed(0)}M`}
                </span>
              ));
            })()}
          </div>

          {/* X-Axis HTML Overlays */}
          <div className="absolute top-full left-0 right-0 h-6 flex justify-between text-[10px] text-muted-foreground font-mono mt-2 pointer-events-none z-10 px-1">
            {[10, 15, 20, 25, 30].map((v) => (
              <span
                key={v}
                style={{
                  position: "absolute",
                  left: `${((v - 10) / 20) * 100}%`,
                  transform: "translateX(-50%)",
                }}
              >
                ${v.toFixed(2)}
              </span>
            ))}
          </div>

          {/* Legend */}
          <div className="absolute -bottom-16 left-0 right-0 flex justify-center gap-6 text-xs text-muted-foreground font-sans">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-[#10b981] opacity-60"></div>
              Target Equity Base
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-[#f59e0b] opacity-80"></div>
              Unlocked Earnout
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-[#ef4444] opacity-50"></div>
              Sponsor (At-Risk)
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-[#3b82f6] opacity-80"></div>
              Sponsor (Liquid)
            </div>
          </div>
        </div>
      </div>

      {/* Dynamic Data Insight */}
      <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 flex gap-4 items-start shadow-sm mt-8">
        <div className="p-3 bg-primary/10 rounded-full text-primary mt-1 flex-shrink-0">
          <Lightbulb size={24} />
        </div>
        <div>
          <h4 className="font-bold text-foreground mb-1 text-lg">
            Key Takeaway
          </h4>
          <p className="text-muted-foreground text-lg text-balance leading-relaxed">
            {insightText}
          </p>
        </div>
      </div>
      {/* Disclaimer */}
      <div className="mt-12 text-xs text-muted-foreground/80 leading-relaxed max-w-4xl mx-auto text-center border-t border-border/50 pt-8">
        <p>
          <strong>Disclaimer:</strong> This tool is for informational and
          educational purposes only and does not constitute financial,
          investment, legal, or tax advice. The outputs generated are estimates
          based on specific assumptions and mathematical models. Actual
          transaction structures, dilution effects, and financial returns may
          vary significantly. Please consult with appropriate professional
          advisors before making any business decisions.
        </p>
      </div>
    </div>
  );
}
